import { useCallback, useRef } from "react";

import { useJitsu } from "@jitsu/react";
import toast from "react-hot-toast";
import { useMatch, useParams } from "react-router-dom";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppDispatch } from "@/data/hooks";
import {
  IPastAssessment,
  updateSelectedSubcategorySlug,
  useSelectedSubcategory,
} from "@/data/templates";
import {
  useCopyAssessmentV2Mutation,
  usePastAssessmentsQuery,
} from "@/generated/graphql";
import {
  useBaseCadmusPath,
  useGlobalParams,
  useQueryParam,
} from "@/router/routing";
import {
  useInsertHtmlContent,
  useIsEditorEmpty,
} from "@/ui/templates/editor-hooks";

import { copyLandingPath } from "./copy-landing";
import {
  useCloseAllTemplatesModals,
  useShowEditorContentReplacementModal,
  useShowTemplatesModal,
} from "./modals";
import { useOnCloseTemplatesModal } from "./templates-modal";

/** Open the modal to ask the user to confirm they want to overwrite their editor content with a template. */
export const useOnSelectTemplate = () => {
  const showEditorContentReplacementModal =
    useShowEditorContentReplacementModal();
  const selectedSubcategory = useSelectedSubcategory();
  const isEditorEmpty = useIsEditorEmpty();
  const insertContent = useInsertHtmlContent();
  const { track } = useJitsu();
  const closeTemplatesModals = useCloseAllTemplatesModals();
  return useCallback(
    (templateId: string) => {
      const template = selectedSubcategory?.templates.find(
        (t) => t.id === templateId
      );
      if (template) {
        if (isEditorEmpty) {
          insertContent(template.content);
          closeTemplatesModals();
          toast.success(`'${template.title}' template has been added`);
          if (selectedSubcategory) {
            track(JitsuEvent.TEMPLATE_USED, {
              template_id: template.id,
              template_title: template.title,
              subcategory_id: selectedSubcategory.id,
              subcategory_title: selectedSubcategory.title,
            });
          }
          return;
        }
        showEditorContentReplacementModal({
          useCase: { type: "template-replacement", template },
        });
      }
    },
    [
      showEditorContentReplacementModal,
      selectedSubcategory,
      isEditorEmpty,
      track,
      insertContent,
      closeTemplatesModals,
    ]
  );
};

export const useCopyAssessment = (): ((
  destinationAssessmentId: string,
  _assessmentName: string,
  sourceInstructionSheetId: string
) => Promise<void>) => {
  const [mutate] = useCopyAssessmentV2Mutation();
  const params = useParams();
  const { tenant, assessmentId } = useGlobalParams();
  const baseCadmusPath = useBaseCadmusPath();
  const closeTemplatesModals = useCloseAllTemplatesModals();

  return useCallback(
    async (
      /** Current assessment ID, the one that is receiving the past assessment. */
      destinationAssessmentId: string,
      _assessmentName: string,
      sourceInstructionSheetId: string
    ) => {
      const result = await mutate({
        variables: {
          destinationAssessmentId,
          sourceInstructionSheetId,
          // A hint the server reads only on a group destination. Elsewhere the
          // copied sheet sets the preset, which decides which task is live.
          destinationTaskId: params.taskId ?? null,
        },
      });

      if (result.data != null) {
        closeTemplatesModals();

        if (tenant && assessmentId) {
          // Use window.location to force a full page reload with fresh data
          window.location.href = copyLandingPath(
            baseCadmusPath,
            result.data.copyAssessmentV2
          );
        }
      }
    },
    [mutate, closeTemplatesModals, params, baseCadmusPath, tenant, assessmentId]
  );
};

export const useOnNewInstructionsClick = () => {
  const closeTemplatesModal = useOnCloseTemplatesModal();
  const showEditorContentReplaceModal = useShowEditorContentReplacementModal();
  const isEditorEmpty = useIsEditorEmpty();
  const { track } = useJitsu();

  return useCallback(() => {
    if (isEditorEmpty) {
      track(JitsuEvent.INSTRUCTIONS_CLEARED);
      closeTemplatesModal();
      return;
    }
    showEditorContentReplaceModal({
      useCase: { type: "empty-content-replacement" },
    });
  }, [
    isEditorEmpty,
    closeTemplatesModal,
    showEditorContentReplaceModal,
    track,
  ]);
};

/**
 * Check whether the entered route contains any request to additionally open
 * the templates modal.
 *
 * Setting the `subcategoryslug` in the query param will preselect this
 * subcategory before the launch.
 *
 * Setting the `opentemplates` to `true` in the query param will open the modal.
 *
 * If the user is not in the specified route, null will be returned.
 * @returns Callback if there is a request to open the modal. `null` if nothing is provided.
 */
export const useOpenTemplatesFromQueryParams = () => {
  const hasBeenShown = useRef(false);
  const showTemplatesModal = useShowTemplatesModal();
  const openTemplates = useQueryParam("opentemplates");
  const subcategorySlug = useQueryParam("subcategoryslug");
  const dispatch = useAppDispatch();
  const matches = useMatch({
    caseSensitive: false,
    end: false,
    path: ":tenant/assessment/:assessment_id/task/*",
  });
  const callback = useCallback(() => {
    if (subcategorySlug != null) {
      dispatch(updateSelectedSubcategorySlug({ slug: subcategorySlug }));
    }
    if (openTemplates === "true") {
      hasBeenShown.current = true;
      showTemplatesModal({});
    }
  }, [openTemplates, dispatch, subcategorySlug, showTemplatesModal]);
  return !hasBeenShown.current &&
    (openTemplates != null || subcategorySlug != null) &&
    matches
    ? callback
    : null;
};

/**
 * Get the past assessments by doing a GraphQL query for the current user's past assessments.
 *
 * @param currentAssessmentId The ID of the current assessment. This will be filtered out of the
 *   results. When provided, it is also sent to the server so incompatible sources (e.g. standalone
 *   Multi-format for a group-assessment destination) are excluded server-side.
 */
export const usePastAssessments = (currentAssessmentId: string | null) => {
  const { loading, data } = usePastAssessmentsQuery({
    variables: { assessmentId: currentAssessmentId },
  });

  const pastAssessments: IPastAssessment[] | undefined =
    data?.pastInstructionSheets
      .filter(
        (pastInstructionSheet) =>
          currentAssessmentId !== pastInstructionSheet.assessment.id
      )
      .flatMap(({ assessment, sheet }) => {
        if (!assessment.settings) return [];

        return [
          {
            assessmentId: assessment.id,
            taskId: sheet.taskId,
            instructionSheetVersionId: sheet.serverId,
            taskFormat: sheet.format,
            name: assessment.name,
            subject: assessment.subject,
            settings: assessment.settings,
            taskKind: sheet.kind,
          },
        ];
      });

  return {
    loading,
    pastAssessments,
  };
};
